import { useEffect, useState } from 'react'
import { api } from '../lib/api'

type DetailsPanelProps = {
  id: string | null
  wiki?: string | null
  className?: string
  onClose?: () => void
  mobile?: boolean
}

export default function DetailsPanel({ id, wiki, className = '', onClose, mobile = false }: DetailsPanelProps) {
  const [data, setData] = useState<any | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!id) {
      setData(null)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    api
      .horizonsVectors(id)
      .then((payload) => {
        setData(payload)
        setError(payload?.error ?? null)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err))
        setData(null)
      })
      .finally(() => setLoading(false))
  }, [id])

  if (!id) {
    return null
  }

  const wikiUrl = wiki || (id ? `https://en.wikipedia.org/wiki/${encodeURIComponent(id.replace(/\s+/g, '_'))}` : undefined)
  const hasData = !!(data && !data.error && (data.helio || data.geo))
  const cards: Array<{ label: string; value: string; sub?: string | null }> = hasData
    ? [
        {
          label: 'Heliocentric Distance',
          value: data.helio?.r_au != null ? `${data.helio.r_au.toFixed(3)} AU` : '—',
        },
        {
          label: 'Earth Distance',
          value: data.geo?.delta_au != null ? `${data.geo.delta_au.toFixed(3)} AU` : '—',
          sub: data.geo?.delta_ld ? `${data.geo.delta_ld} LD` : null,
        },
        {
          label: 'Relative Speed (Earth)',
          value:
            data.geo?.rr_kms != null
              ? `${data.geo.rr_kms.toFixed(2)} km/s`
              : data.geo?.vx_kms != null
                ? `${data.geo.vx_kms.toFixed(2)} km/s`
                : '—',
        },
        {
          label: 'Timestamp (UTC)',
          value: data.time ?? '—',
        },
      ]
    : []

  return (
    <div className={['flex h-full flex-col', className].filter(Boolean).join(' ')}>
      {mobile && (
        <div className="flex justify-center py-2">
          <div className="h-1.5 w-12 rounded-full bg-neutral-700" />
        </div>
      )}
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-neutral-400">
          Horizons Snapshot
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-white/10 px-2 py-1 text-xs text-neutral-200 transition hover:border-white/30 hover:bg-white/10"
        >
          Close
        </button>
      </div>
      <div className="flex-1 overflow-auto px-4 py-4 text-sm text-neutral-200">
        {loading && <div className="text-neutral-400">Loading Horizons…</div>}
        {!loading && error && (
          <div className="rounded border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
            {error}
          </div>
        )}
        {!loading && !error && hasData && (
          <div className="grid grid-cols-1 gap-3 text-sm">
            {cards.map((card) => (
              <div key={card.label} className="rounded-lg border border-white/10 bg-white/5 px-3 py-3">
                <div className="text-[11px] uppercase tracking-wide text-neutral-400">{card.label}</div>
                <div className="mt-1 text-base text-white">{card.value}</div>
                {card.sub && <div className="text-xs text-neutral-400">{card.sub}</div>}
              </div>
            ))}
          </div>
        )}
        {!loading && !error && !hasData && (
          <div className="text-neutral-500 text-sm">Horizons data is not available for this selection.</div>
        )}
        {wikiUrl && (
          <a
            className="mt-4 inline-block text-xs text-neutral-400 underline"
            href={wikiUrl}
            target="_blank"
            rel="noreferrer"
          >
            View on Wikipedia
          </a>
        )}
        <a
          className="mt-2 inline-block text-xs text-neutral-400 underline"
          href={`https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=${encodeURIComponent(id)}`}
          target="_blank"
          rel="noreferrer"
        >
          Open in JPL SBDB / Horizons
        </a>
      </div>
    </div>
  )
}
